import React, { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { Col, Drawer, Row } from 'src/antd';
import { MenuOutlined } from '@ant-design/icons';
import PortalSidebar from 'src/layouts/PortalSidebar';
import { createPrefixer } from '../../../utils';
import { WasteHeroLogo1 } from '../../../svgs';

const p = createPrefixer('wh-UI-Responsive-Sidebar-');

const classes = {
  wrapper: p`wrapper`,
  navigationContent: p`navigation-content`,
  mobileNavigationBar: p`mobile-navigation-bar`,
};

export type ResponsiveSidebarProps = {
  logo?: ReactNode;
};

const NoPaddingDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 0px !important;
    overflow-x: hidden;
  }

  .ant-drawer-header-close-only {
    padding: 0px !important;
  }

  .ant-drawer-close {
    display: none;
  }
`;

const ResponsiveSidebarDiv = styled.div`
  background: #f5f5f5;
  width: 100%;

  .${classes.mobileNavigationBar} {
    padding: 1rem;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .wh-mobile-logo {
    display: flex;
    align-items: center;
  }

  .wh-mobile-logo img,
  .wh-mobile-logo svg {
    height: 2rem;
    width: auto;
    max-width: 160px;
    object-fit: contain;
  }

  .ant-btn-icon-only {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 !important;
  }

  .${classes.navigationContent} {
    display: none;
  }

  @media screen and (min-width: 992px) {
    min-height: 100%;
    display: flex;
    flex-direction: column;
    flex: 0 0 280px;
    min-width: 20rem;
    max-width: 20rem;
    width: 20rem;
    overflow-y: auto;
    overflow-x: hidden;
    position: relative;

    /* Stretch the nav Row to the full column height so the inner white
       <aside> (h-full) resolves against a real height instead of collapsing
       to content height and exposing the gray #f5f5f5 background below. */
    .ant-row {
      flex: 1;
      min-height: 0;
    }

    .${classes.navigationContent} {
      display: revert;
    }
  }
`;

const ResponsiveSidebar: React.FC<ResponsiveSidebarProps> = ({ logo }) => {
  const [showMobileNavMenu, setShowMobileNavMenu] = useState<boolean>(false);
  const { pathname } = useLocation();

  // Close the mobile drawer whenever the active route changes. PortalSidebar
  // navigates via history.push, so keying on pathname guarantees the drawer
  // closes on every navigation (a route-derived selectedKeys tuple could be
  // identical across sibling routes and fail to fire).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setShowMobileNavMenu(false);
  }, [pathname]);

  return (
    <ResponsiveSidebarDiv className={classes.wrapper}>
      <Row style={{ height: '100%' }}>
        <Col xs={24} sm={24} md={24} lg={0}>
          <div className={classes.mobileNavigationBar}>
            {!window.WH_SS_NO_LOGO ? (
              <span className="wh-mobile-logo">
                {logo || <WasteHeroLogo1 />}
              </span>
            ) : (
              <div />
            )}
            <MenuOutlined
              style={{ fontSize: '1.25rem' }}
              onClick={() => setShowMobileNavMenu((prevState) => !prevState)}
            />
          </div>
        </Col>
        <Col span={24} className={classes.navigationContent}>
          <PortalSidebar />
        </Col>
      </Row>
      <NoPaddingDrawer
        title=""
        onClose={() => setShowMobileNavMenu(false)}
        open={showMobileNavMenu}
        width={300}
      >
        <PortalSidebar />
      </NoPaddingDrawer>
    </ResponsiveSidebarDiv>
  );
};

export default ResponsiveSidebar;
